import { execFile } from 'node:child_process'
import { promisify } from 'node:util'
import { readFile, writeFile, mkdtemp, mkdir, rm } from 'node:fs/promises'
import { join } from 'node:path'
import { homedir, tmpdir } from 'node:os'
import { getPty } from './pty-loader.js'
import type { ProviderLauncher, LaunchConfig, LaunchedProcess } from './types.js'
import { SessionRecorder } from '../session/recorder.js'
import { resolveBinaryOrFallback } from './resolve-binary.js'

const execFileAsync = promisify(execFile)

/** Official installer puts the binary here (`~/.local/bin/roo`). */
const HOME_ROO = join(homedir(), '.local', 'bin', 'roo')
const HOME_ROO_CLI = join(homedir(), '.roo', 'cli', 'roo')

/**
 * Launcher for Roo Code CLI (official `roo` binary).
 *
 * The VS Code extension was archived in May 2026; the CLI still installs via
 * the GitHub install script. Interactive by default — do not pass `-p/--print`
 * or a positional prompt. Hidden interview rules live in a temp workspace
 * because Roo has no --append-system-prompt flag.
 */
export class RooCodeLauncher implements ProviderLauncher {
  readonly name = 'roo-code'
  readonly displayName = 'Roo Code'
  readonly pricingHint = '🆓 CLI officielle (clé API personnelle ; extension IDE arrêtée)'

  async isInstalled(): Promise<boolean> {
    return (await this.resolveInstalledBinary()) !== null
  }

  async getVersion(): Promise<string | null> {
    const binary = await this.resolveInstalledBinary()
    if (!binary) return null
    try {
      const { stdout } = await execFileAsync(binary, ['--version'])
      return stdout.trim()
    } catch {
      return null
    }
  }

  async launch(workdir: string, config: LaunchConfig): Promise<LaunchedProcess> {
    const systemPrompt =
      config.systemPromptContent ?? (await readFile(config.systemPromptPath, 'utf-8'))

    const agentDir = await mkdtemp(join(tmpdir(), 'tonty-roo-'))
    const instructions = `# Evaluation Instructions

You are evaluating a candidate. Your job is to GUIDE them — propose approaches, help them think.

**You must not write code or fix things yourself.** The candidate must do the coding.

---

# Project Location
The actual project files are at: ${workdir}
You are NOT in the project directory right now. Navigate there when asked:
cd ${workdir}

${systemPrompt}
`
    await writeFile(join(agentDir, 'AGENTS.md'), instructions)
    await mkdir(join(agentDir, '.roo', 'rules'), { recursive: true })
    await writeFile(join(agentDir, '.roo', 'rules', 'tonty.md'), instructions)

    const args: string[] = ['--workspace', agentDir, '--ephemeral']

    if (config.model) {
      args.push('-m', config.model)
    }

    if (config.permissionMode === 'plan') {
      args.push('--mode', 'architect')
    }

    if (config.permissionMode !== 'bypassPermissions' && config.permissionMode !== 'acceptEdits') {
      args.push('-a')
    }

    const recorder = new SessionRecorder()

    const binary = await resolveBinaryOrFallback((await this.resolveInstalledBinary()) ?? 'roo')

    const ptyProc = getPty().spawn(binary, args, {
      name: 'xterm-256color',
      cwd: agentDir,
      cols: process.stdout.columns ?? 80,
      rows: process.stdout.rows ?? 24,
      env: { ...process.env },
    })

    const cleanup = SessionRecorder.bridgePTY(ptyProc, recorder)

    // Do NOT auto-inject a first prompt. The candidate must drive the session.

    return {
      wait: () =>
        new Promise((resolve) => {
          ptyProc.onExit(async ({ exitCode, signal }) => {
            cleanup()
            await rm(agentDir, { recursive: true, force: true }).catch(() => {})
            resolve({ exitCode, signal })
          })
        }),
      kill: async () => {
        cleanup()
        ptyProc.kill()
        await rm(agentDir, { recursive: true, force: true }).catch(() => {})
      },
      recorder,
    }
  }

  private async resolveInstalledBinary(): Promise<string | null> {
    for (const binary of ['roo', HOME_ROO, HOME_ROO_CLI]) {
      try {
        await execFileAsync(binary, ['--version'])
        return binary
      } catch {
        // try next location
      }
    }
    return null
  }
}
